import { type FormEvent } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useTranslation } from "../../context/LanguageContext";
import { track } from "../../utils/analytics";
import { formatDealPrice, type OnbDeal } from "../../utils/onboardingCoverage";
import {
  C,
  FALLBACK_PRODUCT_IMAGES,
  LanguageToggle,
  ONB_GRADIENT,
  OffBadge,
  OnbError,
  OnbPage,
  OnbTopBar,
  ProductImage,
  RatingLine,
  YellowCta,
  ZipField,
} from "./DealsFirstParts";
import { getProductImageUrl } from "../../utils/imageUtils";

interface Props {
  zip: string;
  onZipChange: (zip: string) => void;
  onSubmit: () => void;
  busy?: boolean;
  error?: string | null;
  onSignOut: () => void;
  /** Shown when the shopper came back from the plans to change the ZIP. */
  onBack?: () => void;
  /** Example deals for the card cluster; undefined while loading, empty for the bundled photos. */
  deals: OnbDeal[] | undefined;
  testId?: string;
}

// Card cluster from the approved mock (x, y, width, rotation) in a 700×560 box (desktop)
// and a 390-wide strip (phone).
const DESKTOP_TILES = [
  [0, 40, 190, -6],
  [210, 0, 210, 4],
  [440, 60, 180, -3],
  [40, 274, 200, 5],
  [270, 264, 220, -4],
  [510, 294, 170, 6],
] as const;
const PHONE_TILES = [
  [14, 0, 130, -6],
  [130, -14, 140, 4],
  [252, 6, 124, -3],
] as const;

type Tile = { deal: OnbDeal | null; image: string | null };

function tilesFor(deals: OnbDeal[] | undefined, count: number): Tile[] {
  if (deals === undefined) return Array.from({ length: count }, () => ({ deal: null, image: null }));
  if (deals.length === 0) {
    return Array.from({ length: count }, (_, i) => ({ deal: null, image: FALLBACK_PRODUCT_IMAGES[i % FALLBACK_PRODUCT_IMAGES.length] }));
  }
  return Array.from({ length: count }, (_, i) => ({ deal: deals[i % deals.length], image: deals[i % deals.length].image }));
}

function TiltCard({ tile, phone }: { tile: Tile; phone: boolean }) {
  const loading = !tile.deal && !tile.image;
  return (
    <div
      className="flex h-full flex-col rounded-[20px]"
      style={{ backgroundColor: C.card, boxShadow: C.shadow, padding: phone ? 7 : 10, borderRadius: phone ? 16 : 20 }}
    >
      <div
        className={`relative aspect-square overflow-hidden ${loading ? "animate-pulse" : ""}`}
        style={{ backgroundColor: loading ? C.skeleton : C.tile, borderRadius: phone ? 11 : 14 }}
      >
        {tile.image ? <ProductImage src={getProductImageUrl(tile.image, "400")} inset="14%" /> : null}
        {tile.deal ? (
          <OffBadge
            pct={tile.deal.discountPercent}
            className={phone ? "left-1.5 top-1.5" : "left-2 top-2"}
            style={phone ? { height: 19, fontSize: 11, padding: "0 6px", borderRadius: 6 } : undefined}
          />
        ) : null}
      </div>
      {tile.deal ? (
        <div className={`flex items-baseline gap-1 truncate font-black ${phone ? "px-[3px] pb-px pt-1.5 text-[14px]" : "px-1 pb-0.5 pt-2 text-[17px]"}`} style={{ color: C.green }}>
          {formatDealPrice(tile.deal.price)}
          <s className={`font-medium ${phone ? "text-[10.5px]" : "text-[12px]"}`} style={{ color: C.soft }}>{formatDealPrice(tile.deal.wasPrice)}</s>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Step 1 of the deals-first onboarding: real clearance cards next to a single
 * ZIP field (desktop), or above it (phone), plus the app language.
 */
export default function ZipStepScreen({ zip, onZipChange, onSubmit, busy, error, onSignOut, onBack, deals, testId = "onboarding-zip" }: Props) {
  const { t, currentLanguage, setLanguage } = useTranslation();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!busy) onSubmit();
  };

  const changeLanguage = (lang: "en" | "es") => {
    track("paywall_language_selected", { language: lang });
    setLanguage(lang);
  };

  const desktopTiles = tilesFor(deals, DESKTOP_TILES.length);
  const phoneTiles = tilesFor(deals, PHONE_TILES.length);
  const back = onBack ? (
    <button
      type="button"
      aria-label={t("onboarding2.back")}
      onClick={onBack}
      className="-ml-2 flex h-9 w-9 items-center justify-center rounded-full transition hover:bg-black/5 dark:hover:bg-white/10"
      style={{ color: C.muted }}
    >
      <ArrowLeft size={20} />
    </button>
  ) : null;

  const headline = (
    <h1 className="m-0 text-[36px] font-black leading-[38px] tracking-[-1.3px] lg:text-[54px] lg:leading-[56px] lg:tracking-[-2px]">
      {t("onboarding2.dz_title_1")}{" "}
      <span style={{ backgroundImage: ONB_GRADIENT, WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>
        {t("onboarding2.dz_title_2")}
      </span>
    </h1>
  );

  return (
    <OnbPage testId={testId}>
      {/* Soft purple glow behind the cards. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-[-120px] top-[60px] h-[420px] w-[640px] rounded-full lg:left-auto lg:right-[max(-160px,calc((100vw-1280px)/2-160px))] lg:top-10 lg:h-[760px] lg:w-[760px]"
        style={{ background: `radial-gradient(circle, ${C.glow}, transparent 65%)` }}
      />
      <OnbTopBar onSignOut={onSignOut} left={back} />

      <form onSubmit={submit} noValidate className="relative">
        <div className="mx-auto flex min-h-[calc(100svh-52px)] w-full max-w-[1280px] flex-col lg:min-h-[calc(100svh-86px)] lg:flex-row lg:items-center lg:px-10">
          {/* Phone: three cards on top. */}
          <div aria-hidden="true" className="relative mx-auto mt-10 h-[230px] w-full max-w-[430px] lg:hidden">
            {PHONE_TILES.map(([x, y, size, rotate], i) => (
              <div
                key={i}
                className="absolute"
                style={{ left: `${(x / 390) * 100}%`, top: y, width: `${(size / 390) * 100}%`, transform: `rotate(${rotate}deg)` }}
              >
                <TiltCard tile={phoneTiles[i]} phone />
              </div>
            ))}
          </div>

          <div className="onb-rise flex w-full flex-col gap-3.5 px-[22px] lg:w-[480px] lg:flex-none lg:gap-[18px] lg:px-0 lg:pl-8 lg:pb-16">
            <RatingLine size={13} />
            {headline}
            <p className="m-0 text-[15px] leading-[21px] lg:text-[17px] lg:leading-[25px]" style={{ color: C.muted }}>
              {t("onboarding2.dz_sub")}
            </p>
            <OnbError error={error ?? null} />
            <div className="flex items-center gap-2.5">
              <ZipField
                value={zip}
                onChange={onZipChange}
                placeholder={t("onboarding2.dz_placeholder")}
                label={t("onboarding2.zip_label")}
                invalid={!!error}
                autoFocus
                className="flex-1 lg:w-[260px] lg:flex-none"
              />
              <div className="hidden w-[200px] flex-none lg:block">
                <YellowCta height={60} fontSize={17} busy={busy}>
                  {t("onboarding2.zip_show_deals")}
                  <ArrowRight size={18} strokeWidth={2.6} />
                </YellowCta>
              </div>
            </div>
            <div className="flex items-center gap-3.5 text-[13.5px]" style={{ color: C.soft }}>
              <span className="hidden lg:inline">{t("onboarding2.language_label")}</span>
              <div className="w-full lg:w-[200px]">
                <LanguageToggle value={currentLanguage === "es" ? "es" : "en"} onChange={changeLanguage} label={t("onboarding2.language_label")} />
              </div>
            </div>
          </div>

          {/* Desktop: the tilted card cluster. */}
          <div aria-hidden="true" className="relative hidden h-[560px] flex-1 lg:block">
            <div className="absolute left-6 top-0 h-[560px] w-[700px] origin-top-left scale-[0.72] xl:left-[48px] xl:scale-100">
              {DESKTOP_TILES.map(([x, y, size, rotate], i) => (
                <div key={i} className="absolute" style={{ left: x, top: y, width: size, transform: `rotate(${rotate}deg)` }}>
                  <TiltCard tile={desktopTiles[i]} phone={false} />
                </div>
              ))}
            </div>
          </div>

          {/* Phone: CTA pinned to the bottom. */}
          <div
            className="sticky bottom-0 z-10 mt-auto px-[22px] pb-[max(env(safe-area-inset-bottom),26px)] pt-4 lg:hidden"
            style={{ background: `linear-gradient(to bottom, transparent, ${C.page} 35%)` }}
          >
            <YellowCta busy={busy}>
              {t("onboarding2.zip_show_deals")}
              <ArrowRight size={18} strokeWidth={2.6} />
            </YellowCta>
          </div>
        </div>
      </form>
    </OnbPage>
  );
}
